import { SigningStargateClient } from "@cosmjs/stargate";
import { fromBase64 } from "@cosmjs/encoding";
import { TxRaw } from "cosmjs-types/cosmos/tx/v1beta1/tx";

import { coins } from "../constants";
import { aminoTxToSignDoc, getPubKeyStr } from "../lib/tendermint";
import keplrConfig from "../config/keplr.config";
import { extendedRegistryTypes } from "../config/registry";

const Account = ({ stargateClient }, args, accountPayload, leapOptions) => {
  const getAddress = async () => {
    const accounts = await stargateClient.signer.getAccounts();
    return {
      address: accounts[0].address,
      pubkey: accounts[0].pubkey,
      pubkeyStr: getPubKeyStr(accounts[0].pubkey),
    };
  };

  const signTransaction = async (rawTx) => {
    const { address, pubkey } = await getAddress();
    if (
      leapOptions.signDirect &&
      (leapOptions.signDirectMsgTypes?.includes(rawTx.msgs[0].type) ?? true)
    ) {
      // sign direct
      const { signDoc } = aminoTxToSignDoc(rawTx, stargateClient, {
        ...accountPayload,
        pubkeyBytes: pubkey,
      });
      const { signature, signed } = await stargateClient.signer.signDirect(
        address,
        signDoc
      );
      const txRawPartial = TxRaw.fromPartial({
        bodyBytes: signed.bodyBytes,
        authInfoBytes: signed.authInfoBytes,
        signatures: [fromBase64(signature.signature)],
      });
      const txBytes = TxRaw.encode(txRawPartial).finish();

      return {
        tx_bytes: Array.from(txBytes),
      };
    }
    // sign amino
    const { signature, signed } = await stargateClient.signer.signAmino(
      address,
      rawTx
    );
    return {
      ...signature,
      signed,
    };
  };

  const sign = async (message) => {};

  const verifySignature = async (message, signature) => {};

  return {
    getAddress,
    sign,
    signTransaction,
    verifySignature,
  };
};

const Backend = ({ stargateClient, coinName, leapOptions }) => {
  const genAccount = async (args, accountPayload) =>
    Account({ stargateClient }, args, accountPayload, leapOptions);

  const getAccount = async (args) => {
    const accountPayload = coins[coinName];
    return genAccount(args, accountPayload);
  };

  const cleanUp = async () => {};

  return {
    getAccount,
    cleanUp,
  };
};

Backend.create = async ({ leapOptions = {}, coinName } = {}) => {
  if (!window.leap) {
    throw new Error("Leap wallet is not installed.");
  }
  if (
    window.leap.experimentalSuggestChain &&
    keplrConfig[leapOptions.project]
  ) {
    await window.leap.experimentalSuggestChain({
      ...keplrConfig[leapOptions.project],
      chainId: leapOptions.chainId,
    });
  }
  window.leap.enable(leapOptions.chainId);
  const offlineSigner = window.leap.getOfflineSigner(leapOptions.chainId);
  const stargateClient = await SigningStargateClient.offline(offlineSigner);
  registerRegistry(stargateClient.registry, extendedRegistryTypes);

  return Backend({ stargateClient, coinName, leapOptions });
};

const registerRegistry = (registry, types) => {
  Object.keys(types).forEach((type) => registry.register(type, types[type]));
};

export default Backend;
